import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpRight,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Flame,
  Globe,
  Link2,
  Package,
  PackageCheck,
  PackagePlus,
  RefreshCw,
  Search,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import {
  fetchCrawledProducts,
  linkCrawledOfferToProduct,
  toTitleCase,
  type CrawledProductItem,
} from '../api.js';
import { CreateWooDraftModal } from '../components/CreateWooDraftModal.js';
import { Alert, Badge, Button, Loading, PageHeader, Panel, PanelBody } from '../ui/index.js';

function extractNumericEan(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const digits = ref.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 14 ? digits : null;
}

function formatTnd(millimes: number | null): string {
  if (millimes === null || !Number.isFinite(millimes)) return '—';
  return (millimes / 1000).toFixed(3) + ' TND';
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function CrawledNewProductsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [sourceKey, setSourceKey] = useState<string>('all');
  const [status, setStatus] = useState<'all' | 'missing' | 'existing' | 'recent'>('all');
  const [hasEan, setHasEan] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [brand, setBrand] = useState('');
  const [search, setSearch] = useState('');

  // Draft creation modal state
  const [selectedOfferForDraft, setSelectedOfferForDraft] = useState<CrawledProductItem | null>(null);
  const [lastCreatedDraft, setLastCreatedDraft] = useState<{
    id: number;
    name: string;
    ean: string | null;
    editUrl: string;
  } | null>(null);

  const [feedback, setFeedback] = useState<{ text: string; tone: 'info' | 'alarm' } | null>(null);

  const productsQuery = useQuery({
    queryKey: ['crawled-products', page, sourceKey, status, hasEan, inStockOnly, brand, search],
    queryFn: () =>
      fetchCrawledProducts({
        page,
        limit: 40,
        sourceKey: sourceKey === 'all' ? undefined : sourceKey,
        status: status === 'all' ? undefined : status,
        hasEan,
        inStockOnly,
        brand: brand || undefined,
        q: search || undefined,
      }),
  });

  const linkMutation = useMutation({
    mutationFn: (input: { offerId: number; productId: number; asCanonicalGtin?: boolean }) =>
      linkCrawledOfferToProduct(input),
    onSuccess: () => {
      setFeedback({
        text: 'Offre associée avec succès au produit existant de votre catalogue !',
        tone: 'info',
      });
      void queryClient.invalidateQueries({ queryKey: ['crawled-products'] });
      void queryClient.invalidateQueries({ queryKey: ['review'] });
    },
    onError: (err) => {
      setFeedback({ text: `Erreur rapprochement : ${String(err)}`, tone: 'alarm' });
    },
  });

  const data = productsQuery.data;
  const items = data?.items ?? [];
  const pagination = data?.pagination;
  const stats = data?.stats;

  const handleOpenDraftModal = (item: CrawledProductItem) => {
    setSelectedOfferForDraft(item);
  };

  const handleExportCsv = () => {
    if (items.length === 0) return;
    const headers = [
      'ID_Offre',
      'Source',
      'EAN',
      'Designation',
      'Marque',
      'Prix_TTC_TND',
      'En_Stock',
      'Present_Catalogue',
      'Produit_Associe_SKU',
      'Prix_MaPara_TTC_TND',
      'Ecart_TND',
      'URL_Source',
    ];
    const rows = items.map((i) => [
      i.id,
      `"${i.sourceLabel}"`,
      `"${i.cleanGtin ?? i.gtinRaw ?? ''}"`,
      `"${i.name.replace(/"/g, '""')}"`,
      `"${i.brand ?? ''}"`,
      i.priceTtc !== null ? (i.priceTtc / 1000).toFixed(3) : '',
      i.inStock === true ? 'OUI' : i.inStock === false ? 'NON' : 'INCONNU',
      i.existsInCatalog ? 'OUI' : 'NON',
      `"${i.matchedProduct?.sku ?? ''}"`,
      i.matchedProduct?.regularPriceTtc ? (i.matchedProduct.regularPriceTtc / 1000).toFixed(3) : '',
      i.matchedProduct?.priceDiffTtc ? (i.matchedProduct.priceDiffTtc / 1000).toFixed(3) : '',
      `"${i.url ?? ''}"`,
    ]);
    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `nouveautes-marche-crawlers-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sourceTabs = [
    { key: 'all', label: 'Tous les sites crawlés' },
    { key: 'eden', label: 'edenpharma.tn' },
    { key: 'mycare', label: 'mycare.tn' },
    { key: 'pharma-shop', label: 'pharma-shop.tn' },
    { key: 'parafendri', label: 'parafendri.tn' },
    { key: 'parashop', label: 'parashop.tn' },
    { key: 'paraexpert', label: 'paraexpert.tn' },
  ];

  return (
    <div className="space-y-5 animate-fade">
      <PageHeader
        index="S22"
        eyebrow="GTIN · Veille Concurrentielle & Crawlers"
        title="Nouveautés Marché & Découverte Produits"
        purpose="Surveillance des produits détectés sur les sites concurrents tunisiens (Eden Pharma, MyCare, Pharma-Shop, Parafendri, Parashop, Paraexpert). Ajoutez en 1 clic les références absentes de votre catalogue ou rapprochez les produits existants."
        actions={
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={items.length === 0}
              className="text-xs"
            >
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Exporter CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void queryClient.invalidateQueries({ queryKey: ['crawled-products'] })}
              disabled={productsQuery.isFetching}
              className="text-xs"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${productsQuery.isFetching ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        }
      />

      {feedback !== null && (
        <Alert
          tone={feedback.tone}
          title={feedback.tone === 'info' ? 'Opération réussie' : 'Erreur'}
          actions={
            <button
              type="button"
              onClick={() => setFeedback(null)}
              className="text-xs font-semibold hover:underline"
            >
              Fermer
            </button>
          }
        >
          {feedback.text}
        </Alert>
      )}

      {/* Success banner for newly created WooCommerce draft */}
      {lastCreatedDraft && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-herb-50 border-2 border-herb-600/40 text-herb-900 shadow-xs animate-rise">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-herb-600 text-white flex items-center justify-center shrink-0 shadow-xs">
              <Check className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-sm text-ink">
                Brouillon créé avec succès sur WooCommerce : « {lastCreatedDraft.name} »
              </p>
              <p className="text-xs text-ink-soft">
                ID Produit Woo : #{lastCreatedDraft.id} {lastCreatedDraft.ean ? `· Code-barres EAN : ${lastCreatedDraft.ean}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-auto">
            {lastCreatedDraft.editUrl && (
              <a
                href={lastCreatedDraft.editUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-herb-700 hover:bg-herb-800 text-white font-bold text-xs transition-colors shadow-2xs"
              >
                <span>Éditer sur WooCommerce</span>
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              type="button"
              onClick={() => setLastCreatedDraft(null)}
              className="text-ink-ghost hover:text-ink p-1 rounded-lg cursor-pointer"
              title="Masquer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        <div
          onClick={() => {
            setStatus('missing');
            setPage(1);
          }}
          className={`p-4 rounded-xl border transition-all cursor-pointer select-none ${
            status === 'missing'
              ? 'bg-rust-50/80 border-rust-600/40 shadow-xs ring-1 ring-rust-600/30'
              : 'bg-card border-border hover:border-rust-600/30 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-rust-700 flex items-center gap-1.5">
              <PackagePlus className="h-4 w-4" />
              Absents du catalogue
            </span>
            <Badge tone="alarm">
              À ajouter
            </Badge>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular font-mono text-ink">
              {(stats?.totalMissing ?? 0).toLocaleString('fr-FR')}
            </span>
            <span className="text-xs text-ink-ghost">références</span>
          </div>
          <p className="text-[11px] text-ink-soft mt-1">Détectées sur les sites concurrents mais absentes de MaPara</p>
        </div>

        <div
          onClick={() => {
            setStatus('existing');
            setPage(1);
          }}
          className={`p-4 rounded-xl border transition-all cursor-pointer select-none ${
            status === 'existing'
              ? 'bg-herb-50/80 border-herb-600/40 shadow-xs ring-1 ring-herb-600/30'
              : 'bg-card border-border hover:border-herb-600/30 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-herb-800 flex items-center gap-1.5">
              <PackageCheck className="h-4 w-4" />
              Présents sur MaPara
            </span>
            <Badge tone="good">
              Correspondance
            </Badge>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular font-mono text-ink">
              {(stats?.totalExisting ?? 0).toLocaleString('fr-FR')}
            </span>
            <span className="text-xs text-ink-ghost">références</span>
          </div>
          <p className="text-[11px] text-ink-soft mt-1">Déjà dans votre catalogue avec comparaison de prix</p>
        </div>

        <div
          onClick={() => {
            setStatus('recent');
            setPage(1);
          }}
          className={`p-4 rounded-xl border transition-all cursor-pointer select-none ${
            status === 'recent'
              ? 'bg-sky-50/80 border-sky-600/40 shadow-xs ring-1 ring-sky-600/30'
              : 'bg-card border-border hover:border-sky-600/30 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-sky-700 flex items-center gap-1.5">
              <Flame className="h-4 w-4" />
              Crawls récents (&lt; 48h)
            </span>
            <Badge tone="scan">
              Nouveaux
            </Badge>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular font-mono text-ink">
              {(stats?.totalRecent ?? 0).toLocaleString('fr-FR')}
            </span>
            <span className="text-xs text-ink-ghost">offres</span>
          </div>
          <p className="text-[11px] text-ink-soft mt-1">Offres collectées lors des dernières sessions de crawl</p>
        </div>

        <div
          onClick={() => {
            setStatus('all');
            setPage(1);
          }}
          className={`p-4 rounded-xl border transition-all cursor-pointer select-none ${
            status === 'all'
              ? 'bg-cream/80 border-border/80 shadow-xs ring-1 ring-border'
              : 'bg-card border-border hover:border-ink-soft/40 shadow-2xs'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft flex items-center gap-1.5">
              <Globe className="h-4 w-4" />
              Total pool offres
            </span>
            <Badge tone="muted">
              Tous
            </Badge>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl font-bold tabular font-mono text-ink">
              {(stats?.totalAnalyzed ?? 0).toLocaleString('fr-FR')}
            </span>
            <span className="text-xs text-ink-ghost">offres analysées</span>
          </div>
          <p className="text-[11px] text-ink-soft mt-1">Sur l'ensemble des 4 sites concurrents tunisiens</p>
        </div>
      </div>

      {/* Source Selector Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border/80 pb-2">
        {sourceTabs.map((tab) => {
          const isActive = sourceKey === tab.key;
          const sCounts = tab.key === 'all' ? null : stats?.countsBySource[tab.key];
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setSourceKey(tab.key);
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                isActive
                  ? 'bg-herb-700 text-white font-semibold shadow-xs'
                  : 'bg-card text-ink-soft hover:bg-cream hover:text-ink border border-border'
              }`}
            >
              <span>{tab.label}</span>
              {sCounts !== null && sCounts !== undefined && (
                <span
                  className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                    isActive ? 'bg-white/20 text-white' : 'bg-paper text-ink-ghost'
                  }`}
                >
                  {sCounts.total}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter Toolbar */}
      <Panel>
        <PanelBody className="py-3 px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[240px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-ink-ghost" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Rechercher désignation, EAN, marque…"
                  className="w-full rounded-lg border border-border bg-card pl-9 pr-3 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
                />
              </div>

              <input
                type="text"
                value={brand}
                onChange={(e) => {
                  setBrand(e.target.value);
                  setPage(1);
                }}
                placeholder="Filtrer par marque…"
                className="w-36 rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
              />

              <div className="flex items-center gap-3 border-l border-border/80 pl-3">
                <label className="flex items-center gap-1.5 text-xs font-medium text-ink cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hasEan}
                    onChange={(e) => {
                      setHasEan(e.target.checked);
                      setPage(1);
                    }}
                    className="rounded border-border text-herb-700 focus:ring-herb-600"
                  />
                  <span>Avec code EAN uniquement</span>
                </label>

                <label className="flex items-center gap-1.5 text-xs font-medium text-ink cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => {
                      setInStockOnly(e.target.checked);
                      setPage(1);
                    }}
                    className="rounded border-border text-herb-700 focus:ring-herb-600"
                  />
                  <span>En stock uniquement</span>
                </label>
              </div>
            </div>

            <div className="text-xs text-ink-ghost font-mono">
              Page {pagination?.page ?? 1} / {pagination?.totalPages ?? 1} ({pagination?.total ?? 0} résultats)
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Main Table */}
      {productsQuery.isLoading ? (
        <Loading label="Recherche des produits crawlés…" />
      ) : items.length === 0 ? (
        <Panel>
          <PanelBody className="py-12 text-center">
            <CheckCircle2 className="h-8 w-8 mx-auto text-herb-600 mb-2" />
            <p className="text-sm font-semibold text-ink">Aucun produit ne correspond aux filtres sélectionnés.</p>
            <p className="text-xs text-ink-faint mt-1">
              Essayez de réinitialiser la recherche ou de changer de statut/site.
            </p>
          </PanelBody>
        </Panel>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-xs">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-border bg-paper/60 text-ink-ghost text-[11px] uppercase tracking-wider font-semibold">
                <th className="py-3 px-3.5">Visuel & Code EAN</th>
                <th className="py-3 px-3">Désignation Produit (Crawler)</th>
                <th className="py-3 px-3">Site Source</th>
                <th className="py-3 px-3 text-right">Prix Concurrent TTC</th>
                <th className="py-3 px-3 text-center">Statut Catalogue MaPara</th>
                <th className="py-3 px-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {items.map((item) => {
                const priceConcurrent = item.priceTtc;
                const matched = item.matchedProduct;

                return (
                  <tr key={item.id} className="hover:bg-cream/40 transition-colors">
                    {/* Visual & EAN */}
                    <td className="py-3 px-3.5 align-middle">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-paper border border-border/70 overflow-hidden flex items-center justify-center shrink-0">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="h-full w-full object-contain"
                              onError={(e) => {
                                (e.target as HTMLElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <Package className="h-5 w-5 text-ink-ghost" />
                          )}
                        </div>
                        <div>
                          {(() => {
                            const effectiveEan =
                              item.cleanGtin ??
                              extractNumericEan(item.gtinRaw) ??
                              extractNumericEan(item.referenceRaw) ??
                              extractNumericEan(item.externalRef);
                            if (effectiveEan) {
                              return (
                                <span className="font-mono text-xs font-bold text-ink flex items-center gap-1">
                                  <Badge tone="good" className="font-mono text-[10px]">
                                    {effectiveEan.length === 8 ? 'EAN-8' : 'EAN-13'}
                                  </Badge>
                                  {effectiveEan}
                                </span>
                              );
                            }
                            if (item.gtinRaw) {
                              return <span className="font-mono text-xs text-ink-soft">{item.gtinRaw}</span>;
                            }
                            return <span className="text-[11px] text-ink-ghost italic">Sans code-barres</span>;
                          })()}
                          {item.referenceRaw && (!extractNumericEan(item.referenceRaw) || item.referenceRaw !== (item.cleanGtin ?? extractNumericEan(item.referenceRaw))) && (
                            <p className="font-mono text-[10px] text-ink-ghost mt-0.5">Réf: {item.referenceRaw}</p>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Designation & Brand */}
                    <td className="py-3 px-3 align-middle max-w-sm">
                      <div className="font-medium text-ink leading-snug line-clamp-2">{item.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        {item.brand && (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-paper text-ink-soft border border-border">
                            {item.brand}
                          </span>
                        )}
                        {item.isRecent && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded-full">
                            <Flame className="h-3 w-3" /> Nouveau crawl
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Source Site */}
                    <td className="py-3 px-3 align-middle">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-xs text-ink">{item.sourceLabel}</span>
                        {item.url && (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-ink-ghost hover:text-herb-700 transition-colors p-0.5"
                            title="Ouvrir la fiche source"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                      <p className="text-[10px] text-ink-ghost mt-0.5">{formatDate(item.fetchedAt)}</p>
                    </td>

                    {/* Price & Stock */}
                    <td className="py-3 px-3 align-middle text-right">
                      <div className="font-mono font-bold text-xs text-ink">{formatTnd(priceConcurrent)}</div>
                      <div className="mt-0.5">
                        {item.inStock === true ? (
                          <span className="text-[10px] font-medium text-herb-700">En stock</span>
                        ) : item.inStock === false ? (
                          <span className="text-[10px] font-medium text-rust-700">Rupture</span>
                        ) : (
                          <span className="text-[10px] text-ink-ghost">—</span>
                        )}
                      </div>
                    </td>

                    {/* Catalog Status & Price comparison */}
                    <td className="py-3 px-3 align-middle">
                      {item.existsInCatalog && matched ? (
                        <div className="p-2 rounded-lg bg-herb-50/70 border border-herb-600/20 max-w-xs">
                          <div className="flex items-center justify-between gap-1">
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-herb-800">
                              <Check className="h-3.5 w-3.5" /> Présent sur MaPara
                            </span>
                            <span className="font-mono text-[10px] text-herb-900 font-semibold">{matched.sku}</span>
                          </div>
                          <p className="text-[11px] text-ink line-clamp-1 mt-0.5 font-medium">{matched.name}</p>
                          <div className="mt-1 flex items-center justify-between text-[10px] border-t border-herb-600/20 pt-1">
                            <span className="text-ink-soft">Prix MaPara : {formatTnd(matched.regularPriceTtc)}</span>
                            {matched.priceDiffTtc !== null && (
                              <span
                                className={`font-mono font-semibold flex items-center gap-0.5 ${
                                  matched.priceDiffTtc > 0
                                    ? 'text-rust-700'
                                    : matched.priceDiffTtc < 0
                                      ? 'text-herb-800'
                                      : 'text-ink-soft'
                                }`}
                              >
                                {matched.priceDiffTtc > 0 ? (
                                  <>
                                    <TrendingUp className="h-3 w-3" /> +{(matched.priceDiffTtc / 1000).toFixed(3)} TND
                                  </>
                                ) : (
                                  <>
                                    <TrendingDown className="h-3 w-3" /> {(matched.priceDiffTtc / 1000).toFixed(3)} TND
                                  </>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rust-50 text-rust-700 border border-rust-600/20">
                            <PackagePlus className="h-3 w-3" /> Absent du catalogue
                          </span>
                          <p className="text-[10px] text-ink-ghost mt-0.5">Non trouvé par EAN ou nom</p>
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-3 align-middle text-center">
                      {item.existsInCatalog && matched ? (
                        <div className="flex flex-col gap-1.5 items-center">
                          {!item.isLinked ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              onClick={() =>
                                linkMutation.mutate({
                                  offerId: item.id,
                                  productId: matched.id,
                                  asCanonicalGtin: !matched.gtinCanonical && !!item.cleanGtin,
                                })
                              }
                              disabled={linkMutation.isPending}
                              className="text-[11px] text-herb-800 hover:bg-herb-50 border-herb-600/40 w-full justify-center"
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              Lier l'offre
                            </Button>
                          ) : (
                            <span className="text-[11px] text-herb-800 font-semibold flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3 text-herb-700" /> Déjà liée
                            </span>
                          )}
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => handleOpenDraftModal(item)}
                            title="Créer un nouveau produit brouillon sur WooCommerce si cette correspondance est inexacte"
                            className="text-[11px] text-herb-900 border-border hover:border-herb-600/50 hover:bg-herb-50/80 w-full justify-center font-medium shadow-2xs"
                          >
                            <PackagePlus className="h-3 w-3 mr-1 text-herb-700" />
                            Créer un brouillon
                          </Button>
                          <a
                            href={`/workbench?q=${encodeURIComponent(matched.sku)}`}
                            className="text-[10px] text-ink-soft hover:text-herb-800 hover:underline flex items-center gap-0.5"
                          >
                            <span>Voir Workbench</span>
                            <ArrowUpRight className="h-2.5 w-2.5" />
                          </a>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="primary"
                          size="xs"
                          onClick={() => handleOpenDraftModal(item)}
                          className="bg-herb-700 hover:bg-herb-800 text-white font-semibold text-[11px] shadow-2xs whitespace-nowrap"
                        >
                          <PackagePlus className="h-3.5 w-3.5 mr-1" />
                          + Ajouter au catalogue
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border/80 pt-3 text-xs">
          <div className="text-ink-soft">
            Affichage des résultats {(page - 1) * pagination.limit + 1} à{' '}
            {Math.min(page * pagination.limit, pagination.total)} sur {pagination.total.toLocaleString('fr-FR')}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="text-xs"
            >
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />
              Précédent
            </Button>
            <span className="font-mono text-xs px-2 font-semibold text-ink">
              {page} / {pagination.totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="text-xs"
            >
              Suivant
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Modal: Créer un Brouillon dans WooCommerce (Centered & Portaled to body) */}
      <CreateWooDraftModal
        isOpen={selectedOfferForDraft !== null}
        onClose={() => setSelectedOfferForDraft(null)}
        subtitle={`Nouveauté Marché · Détecté sur ${selectedOfferForDraft?.sourceLabel ?? 'Crawler'}`}
        initialData={
          selectedOfferForDraft
            ? {
                name: toTitleCase(selectedOfferForDraft.name),
                ean:
                  selectedOfferForDraft.cleanGtin ??
                  extractNumericEan(selectedOfferForDraft.gtinRaw) ??
                  extractNumericEan(selectedOfferForDraft.referenceRaw) ??
                  extractNumericEan(selectedOfferForDraft.externalRef) ??
                  '',
                brand: selectedOfferForDraft.brand ? toTitleCase(selectedOfferForDraft.brand) : '',
                regularPrice:
                  selectedOfferForDraft.priceTtc !== null
                    ? (selectedOfferForDraft.priceTtc / 1000).toFixed(3)
                    : '',
                imageUrl: selectedOfferForDraft.image ?? '',
                sku: '',
                offerId: selectedOfferForDraft.id,
              }
            : null
        }
        onSuccess={(data) => {
          setFeedback({
            text: `✓ Produit brouillon #${data.wcProductId} créé avec succès sur WooCommerce : « ${data.name} » !`,
            tone: 'info',
          });
          setLastCreatedDraft({
            id: data.wcProductId,
            name: data.name,
            ean: data.ean,
            editUrl: data.editUrl,
          });
          setSelectedOfferForDraft(null);
          void queryClient.invalidateQueries({ queryKey: ['crawled-products'] });
          void queryClient.invalidateQueries({ queryKey: ['kpis'] });
          void queryClient.invalidateQueries({ queryKey: ['review'] });
        }}
      />
    </div>
  );
}
